import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import siteCss from './siteStyles';
import { WhatsAppFab } from './SiteExtras';
import api from '../api/client';
import logo from '../assets/logo-narayan.png';
import { useAuth } from '../context/AuthContext';

/* Scroll-triggered fade-up. Uses IntersectionObserver rather than pulling an
   animation library in for one page. */
export function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setShown(true); return undefined; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${shown ? 'in' : ''}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const InstagramIcon = () => (
  <Icon size={18} d={<><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><line x1="17.5" y1="6.5" x2="17.5" y2="6.5" /></>} />
);
const FacebookIcon = () => (
  <Icon size={18} d={<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />} />
);
const MailIcon = () => (
  <Icon size={14} d={<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /></>} />
);
const PhoneIcon = () => (
  <Icon size={14} d={<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.4 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />} />
);

/**
 * Public website shell — injects the site CSS, renders the sticky nav (with the
 * Login entry into the web app) and the admin-controlled footer.
 * Pass `settings` if the page already loaded them, otherwise the shell fetches.
 */
export default function SiteShell({ children, settings }) {
  const [s, setS] = useState(settings || {});
  const { isAuthed } = useAuth();

  useEffect(() => { if (settings) setS(settings); }, [settings]);
  useEffect(() => {
    if (settings) return;
    api.get('/settings/public').then((d) => setS(d || {})).catch(() => {});
  }, [settings]);

  const name = s.companyName || 'Narayan Home Facility Services';
  const year = new Date().getFullYear();

  return (
    <div className="site">
      <style>{siteCss}</style>

      {/* Nav */}
      <nav className="nav">
        <div className="wrap nav-in">
          <Link to="/"><img className="nav-logo" src={logo} alt={name} /></Link>

          <div className="nav-links">
            <a href="/#services">Services</a>
            <a href="/#how">How it works</a>
            <Link to="/gallery">Gallery</Link>
            <a href="/#reviews">Reviews</a>
            <a href="/#faq">FAQ</a>
          </div>

          <div className="nav-actions">
            {s.playStoreUrl
              ? <a className="btn btn-ghost" href={s.playStoreUrl} target="_blank" rel="noreferrer">Get the App</a>
              : <a className="btn btn-ghost" href="/#download">Get the App</a>}
            {/* Already signed in? Go straight through to the app. */}
            <Link className="btn btn-primary" to={isAuthed ? '/app' : '/login'}>
              {isAuthed ? 'Open App' : 'Login'}
            </Link>
          </div>
        </div>
      </nav>

      {children}

      {/* Footer — all links/contacts come from admin settings */}
      <footer className="foot">
        <div className="wrap">
          <div className="foot-grid">
            <div>
              <img className="foot-logo" src={logo} alt={name} />
              <p>{name}</p>
              {s.companyAddress ? <p style={{ marginTop: 6 }}>{s.companyAddress}</p> : null}
              <div className="socials">
                {s.socialInstagram ? <a href={s.socialInstagram} target="_blank" rel="noreferrer" aria-label="Instagram"><InstagramIcon /></a> : null}
                {s.socialFacebook ? <a href={s.socialFacebook} target="_blank" rel="noreferrer" aria-label="Facebook"><FacebookIcon /></a> : null}
              </div>
            </div>

            <div>
              <h4>Company</h4>
              <a href="/#services">Services</a>
              <a href="/#how">How it works</a>
              <Link to="/gallery">Gallery</Link>
              <a href="/#reviews">Reviews</a>
              <a href="/#faq">FAQ</a>
            </div>

            <div>
              <h4>Legal</h4>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/delete-account">Delete Account</Link>
              {s.playStoreUrl ? <a href={s.playStoreUrl} target="_blank" rel="noreferrer">Google Play</a> : null}
            </div>

            <div>
              <h4>Contact</h4>
              {s.companyPhone ? (
                <a href={`tel:${s.companyPhone}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PhoneIcon /> {s.companyPhone}
                </a>
              ) : null}
              {s.supportEmail ? (
                <a href={`mailto:${s.supportEmail}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MailIcon /> {s.supportEmail}
                </a>
              ) : null}
              {s.companyGst ? <p style={{ fontSize: 12.5 }}>GSTIN: {s.companyGst}</p> : null}
            </div>
          </div>

          <div className="foot-bot">
            <span>© 2012–{year} {name}. All rights reserved.</span>
            <span>Made with care in India 🇮🇳</span>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button — admin sets the number in Settings
          (whatsappNumber); falls back to the constant in SiteExtras.js. */}
      <WhatsAppFab number={s.whatsappNumber} />
    </div>
  );
}
